import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import {
  useDeleteTemplate,
  useTemplate,
  useUpdateTemplate,
} from "@/features/templates/hooks";
import type {
  LibraryTemplate,
  TemplateDirection,
  TemplateVariable,
} from "@/features/templates/api";

export default function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "member");
  const canEdit = hasMinRole(role, "manager");

  const q = useTemplate(id);
  const update = useUpdateTemplate(id!);
  const del = useDeleteTemplate();

  const [form, setForm] = useState<LibraryTemplate | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  function set<K extends keyof LibraryTemplate>(key: K, value: LibraryTemplate[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  // Platform templates are superuser-managed (via admin); view-only here.
  const locked = !canEdit || !!form?.is_platform;

  async function save() {
    if (!form) return;
    try {
      await update.mutateAsync({
        name: form.name,
        description: form.description,
        category: form.category,
        direction: form.direction,
        system_prompt: form.system_prompt,
        greeting: form.greeting,
        rules: form.rules,
        tools: form.tools,
        status: form.status,
        is_active: form.is_active,
      });
      toast.success("Template saved");
    } catch (err) {
      const x = err as { response?: { data?: { error?: string } } };
      toast.error(x?.response?.data?.error || "Failed to save");
    }
  }

  async function confirmDelete() {
    if (!id) return;
    try {
      await del.mutateAsync(id);
      toast.success("Template deleted");
      navigate("/templates");
    } catch {
      toast.error("Failed to delete");
    }
  }

  if (q.isLoading || !form) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => navigate("/templates")}
            aria-label="Back"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{form.name}</h1>
            <p className="text-xs text-muted-foreground">
              {form.is_platform ? "Platform template" : "Workspace template"} ·{" "}
              {form.direction} · {form.source}
            </p>
          </div>
        </div>
        {!locked && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="size-4" />
              Delete
            </Button>
            <Button onClick={save} disabled={update.isPending}>
              <Save className="size-4" />
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>

      {locked && (
        <Card>
          <CardContent className="py-3 text-sm text-muted-foreground">
            This is a platform template managed by administrators — view only.
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="prompt">
        <TabsList>
          <TabsTrigger value="prompt">Prompt</TabsTrigger>
          <TabsTrigger value="variables">
            Variables ({form.variables.filter((v) => v.scope === "user").length})
          </TabsTrigger>
          <TabsTrigger value="flow">
            Flow ({form.flow?.steps?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
          <TabsTrigger value="faq">FAQ ({form.faq?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
        </TabsList>

        <TabsContent value="prompt" className="space-y-4 pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Name</Label>
              <Input value={form.name} disabled={locked} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                disabled={locked}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input value={form.category} disabled={locked} onChange={(e) => set("category", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Direction</Label>
              <Select
                value={form.direction}
                disabled={locked}
                onValueChange={(v) => set("direction", v as TemplateDirection)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Greeting</Label>
            <Textarea
              rows={2}
              value={form.greeting}
              disabled={locked}
              onChange={(e) => set("greeting", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>System prompt</Label>
            <Textarea
              rows={18}
              value={form.system_prompt}
              disabled={locked}
              className="font-mono text-xs"
              onChange={(e) => set("system_prompt", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Variables update automatically when you save.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="variables" className="pt-4">
          <VariablesTab variables={form.variables} />
        </TabsContent>

        <TabsContent value="flow" className="pt-4">
          {(form.flow?.steps?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No flow steps were parsed from this template.
            </p>
          ) : (
            <ol className="space-y-2">
              {form.flow.steps!.map((s, i) => (
                <li key={i} className="rounded-md border p-3">
                  <p className="text-sm font-medium">{s.step}</p>
                  {s.text && (
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                      {s.text}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Ordered steps captured from the document. Visual graph + execution
            come in a later phase.
          </p>
        </TabsContent>

        <TabsContent value="faq" className="pt-4">
          {(form.faq?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No FAQ / knowledge-base entries were parsed.
            </p>
          ) : (
            <div className="space-y-2">
              {form.faq.map((qa, i) => (
                <div key={i} className="rounded-md border p-3">
                  <p className="text-sm font-medium">{qa.q}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {qa.a}
                  </p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tools" className="space-y-4 pt-4">
          <div>
            <Label>Tools referenced</Label>
            {form.tools.length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">None declared.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.tools.map((t) => (
                  <span key={t} className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">
                    {t}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Declared only in this phase — not yet wired to real actions.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="rules" className="pt-4">
          <div className="space-y-1.5">
            <Label>Rules / compliance block</Label>
            <Textarea
              rows={12}
              value={form.rules}
              disabled={locked}
              className="font-mono text-xs"
              onChange={(e) => set("rules", e.target.value)}
              placeholder="Guardrails, compliance and safety rules kept separate from the editable prompt."
            />
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <span className="font-medium text-foreground">{form.name}</span>? This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function VariablesTab({ variables }: { variables: TemplateVariable[] }) {
  const userVars = variables.filter((v) => v.scope === "user");
  const systemVars = variables.filter((v) => v.scope === "system");

  if (variables.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No variables detected. Add <code>{"{{placeholders}}"}</code> to the prompt and save.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <VarTable
        title="Fill-in fields"
        subtitle="The agent's owner enters these when adopting the template."
        rows={userVars}
      />
      {systemVars.length > 0 && (
        <VarTable
          title="Auto-filled by the platform"
          subtitle="Injected at call time — no input needed."
          rows={systemVars}
        />
      )}
    </div>
  );
}

function VarTable({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle: string;
  rows: TemplateVariable[];
}) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-0">
        <div className="border-b px-4 py-3">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Variable</th>
              <th className="px-4 py-2 font-medium">Label</th>
              <th className="px-4 py-2 font-medium">Group</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((v) => (
              <tr key={v.name}>
                <td className="px-4 py-2 font-mono text-xs">{v.name}</td>
                <td className="px-4 py-2">{v.label}</td>
                <td className="px-4 py-2 text-muted-foreground">{v.group}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
